import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Plus } from 'lucide-react'
import type { Connector, ConnectorType } from '@/types'
import {
  CONNECTOR_LABELS,
  CONNECTOR_DESCRIPTIONS,
  CONNECTOR_OVERVIEWS,
  CONNECTOR_HOW_IT_WORKS,
  REPOSITORY_CONNECTOR_TYPES,
} from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'
import { RepositoryPicker } from './RepositoryPicker'
import { WikiPicker } from './WikiPicker'
import { DisconnectConfirmDialog } from './DisconnectConfirmDialog'

interface ConnectorDetailViewProps {
  connectorType: ConnectorType | null
  connector: Connector | undefined
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onConnect: (type: ConnectorType) => void
  onDisconnect: (type: ConnectorType) => void
  isConnecting: boolean
  isDisconnecting: boolean
  canManage: boolean
}

export function ConnectorDetailView({
  connectorType,
  connector,
  projectId,
  open,
  onOpenChange,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
  canManage,
}: ConnectorDetailViewProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (!connectorType) return null

  const Icon = CONNECTOR_ICONS[connectorType]
  const isConnected = connector !== undefined
  const label = CONNECTOR_LABELS[connectorType]

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-screen h-screen m-0 rounded-none data-[state=open]:slide-in-from-bottom-0 data-[state=closed]:slide-out-to-bottom-0 left-0 top-0 translate-x-0 translate-y-0 flex flex-col p-0 gap-0 border-0"
      >
        <DialogHeader className="flex-none border-b px-6 py-4">
          <DialogTitle>
            {isConnected ? label : 'Enable Connector'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted shrink-0">
                <Icon className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-semibold">{label}</h2>
                  {isConnected && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-success text-success-foreground border-success-foreground" data-testid="connector-connected-status">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {CONNECTOR_DESCRIPTIONS[connectorType]}
                </p>
                {canManage && (
                  <div className="mt-3">
                    {isConnected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmOpen(true)}
                        className="bg-danger text-danger-foreground border-danger-foreground hover:bg-danger hover:text-danger-foreground"
                        data-testid="disconnect-connector-button"
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => onConnect(connectorType)}
                        disabled={isConnecting}
                        data-testid="enable-connector-button"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {isConnecting ? 'Enabling...' : 'Enable'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Overview</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {CONNECTOR_OVERVIEWS[connectorType]}
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold">How It Works</h3>
              <ol className="list-decimal list-inside space-y-2">
                {CONNECTOR_HOW_IT_WORKS[connectorType].map((step, index) => (
                  <li key={index} className="text-sm text-muted-foreground leading-relaxed">
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            {isConnected && REPOSITORY_CONNECTOR_TYPES.includes(connectorType) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Repositories</h3>
                  <RepositoryPicker
                    projectId={projectId}
                    connectorType={connectorType}
                    onConnect={() => onConnect(connectorType)}
                  />
                </div>
              </>
            )}

            {isConnected && connectorType === 'AzureDevOps' && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">Wikis</h3>
                  <WikiPicker projectId={projectId} enabled={open} />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    <DisconnectConfirmDialog
      projectId={projectId}
      connectorType={connectorType}
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      onConfirm={() => {
        setConfirmOpen(false)
        onDisconnect(connectorType)
      }}
      isDisconnecting={isDisconnecting}
    />
    </>
  )
}
